import { getToken } from '../services/keycloak';
import { loggingService } from '../services/loggingService';
import { appConfig } from '../config/appConfig';

const API_BASE_URL = appConfig.apiBaseUrl;

// Helper function to get headers with authentication token
const getHeaders = async (): Promise<HeadersInit> => {
  const token = await getToken();
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  } else {
    console.warn('[API] No token available for request');
  }
  
  return headers;
};

export interface Message {
  id: number;
  senderId: string;
  receiverId: string;
  encryptedContent: string;
  mac: string;
  timestamp: string;
  // PERFORMANCE FIX: Server-side decrypted content
  content?: string; // Decrypted content (if server decrypted it)
  decryptionError?: string; // Error message if server decryption failed
}

export interface CreateMessageRequest {
  senderId: string;
  receiverId: string;
  encryptedContent: string;
  mac: string;
}

export interface KeyPairResponse {
  // SECURITY FIX: Private keys are no longer returned
  // publicKey: string; // Only public key is returned
  publicKey: string;
}

export interface SharedKeyResponse {
  // SECURITY FIX: Shared keys are no longer returned
  // Clients must derive keys locally
  message: string;
}

export interface PaginatedMessagesResponse {
  messages: Message[];
  page: number;
  pageSize: number;
  totalCount: number;
  decrypted?: boolean; // True if messages were decrypted server-side
  decryptionFailed?: boolean; // True if server-side decryption failed
}

export interface UserResponse {
  userId: string;
  username: string;
}

export const api = {
  async generateKeyPair(clientId?: string): Promise<KeyPairResponse> {
    loggingService.trackApiCall('POST', '/keys/generate', { clientId: clientId || 'none' });
    
    try {
      const headers = await getHeaders();
      // Backend expects PublicKeyRequest format, but PublicKey is optional for generation
      // Send empty PublicKey to satisfy validation (backend generates it)
      const response = await fetch(`${API_BASE_URL}/keys/generate`, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({ 
          clientId: clientId || '',
          publicKey: '' // Empty string to satisfy validation - backend will generate it
        }),
      });

      if (!response.ok) {
        let errorMessage = 'Failed to generate key pair';
        let errorDetails: any = null;
        let fullErrorText = '';
        try {
          errorDetails = await response.json();
          // Extract detailed error information
          if (errorDetails.errors) {
            // Validation errors from ASP.NET Core
            const validationErrors = Object.entries(errorDetails.errors)
              .map(([key, value]: [string, any]) => `${key}: ${Array.isArray(value) ? value.join(', ') : value}`)
              .join('; ');
            errorMessage = `Validation Error: ${validationErrors}`;
            fullErrorText = `HTTP ${response.status} ${response.statusText}\n\nValidation Errors:\n${validationErrors}\n\nFull Response: ${JSON.stringify(errorDetails, null, 2)}`;
          } else if (errorDetails.error) {
            errorMessage = errorDetails.error;
            fullErrorText = `HTTP ${response.status} ${response.statusText}\n\nError: ${errorDetails.error}\n\nFull Response: ${JSON.stringify(errorDetails, null, 2)}`;
          } else if (errorDetails.title) {
            errorMessage = errorDetails.title;
            fullErrorText = `HTTP ${response.status} ${response.statusText}\n\nTitle: ${errorDetails.title}\n\nFull Response: ${JSON.stringify(errorDetails, null, 2)}`;
          } else {
            fullErrorText = `HTTP ${response.status} ${response.statusText}\n\nFull Response: ${JSON.stringify(errorDetails, null, 2)}`;
          }
        } catch {
          errorMessage = `HTTP ${response.status}: ${response.statusText}`;
          fullErrorText = `HTTP ${response.status}: ${response.statusText}`;
        }
        
        loggingService.trackApiCall('POST', '/keys/generate', { clientId }, null, {
          error: errorMessage,
          status: response.status,
          statusText: response.statusText,
          errorDetails,
          fullErrorText,
        });
        
        const error = new Error(errorMessage);
        (error as any).response = response;
        (error as any).status = response.status;
        (error as any).statusText = response.statusText;
        (error as any).errorDetails = errorDetails;
        (error as any).fullErrorText = fullErrorText;
        throw error;
      }

      const data = await response.json();
      loggingService.trackApiCall('POST', '/keys/generate', { clientId }, {
        publicKeyLength: data.publicKey?.length || 0,
      });
      
      // SECURITY FIX: Backend no longer returns private keys
      // Private keys are stored server-side only
      return {
        publicKey: data.publicKey,
        // privateKey is intentionally omitted - stored server-side
      };
    } catch (err: any) {
      // Handle various network errors
      if (err instanceof TypeError) {
        if (err.message.includes('fetch') || err.message.includes('Failed to fetch')) {
          const networkError = 'Network error: Could not connect to server. Make sure the backend is running on http://localhost:5000';
          loggingService.error('API', 'Network error in generateKeyPair', { error: networkError });
          throw new Error(networkError);
        }
        if (err.message.includes('sending the request')) {
          const networkError = 'Network error: Request failed. Check your internet connection and ensure the backend is running.';
          loggingService.error('API', 'Network error in generateKeyPair', { error: networkError });
          throw new Error(networkError);
        }
      }
      if (err instanceof Error) {
        loggingService.error('API', 'Error in generateKeyPair', {
          error: err.message,
          stack: err.stack,
          status: (err as any).status,
        });
        throw err;
      }
      const genericError = err?.message || 'Failed to generate key pair';
      loggingService.error('API', 'Unknown error in generateKeyPair', { error: genericError });
      throw new Error(genericError);
    }
  },

  async registerPublicKey(clientId: string, publicKey: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/keys`, {
      method: 'POST',
      headers: await getHeaders(),
      body: JSON.stringify({ clientId, publicKey }),
    });

    if (!response.ok) {
      throw new Error('Failed to register public key');
    }
  },

  async getPublicKey(clientId: string): Promise<string> {
    loggingService.trackApiCall('GET', `/keys/${clientId}`);
    
    try {
      const headers = await getHeaders();
      const response = await fetch(`${API_BASE_URL}/keys/${clientId}`, {
        headers: headers,
      });
      
      if (!response.ok) {
        if (response.status === 404) {
          const notFoundError = `Public key not found for client "${clientId}". They need to log in first.`;
          loggingService.trackApiCall('GET', `/keys/${clientId}`, null, null, {
            error: notFoundError,
            status: 404,
          });
          throw new Error(notFoundError);
        }
        const error = new Error('Failed to retrieve public key');
        (error as any).response = response;
        (error as any).status = response.status;
        loggingService.trackApiCall('GET', `/keys/${clientId}`, null, null, {
          error: 'Failed to retrieve public key',
          status: response.status,
        });
        throw error;
      }
      
      const data = await response.json();
      loggingService.trackApiCall('GET', `/keys/${clientId}`, null, {
        publicKeyLength: data.publicKey?.length || 0,
      });
      return data.publicKey;
    } catch (err: any) {
      // Handle various network errors
      if (err instanceof TypeError) {
        if (err.message.includes('fetch') || err.message.includes('Failed to fetch')) {
          const networkError = 'Network error: Could not connect to server. Make sure the backend is running on http://localhost:5000';
          loggingService.error('API', 'Network error in getPublicKey', { error: networkError });
          throw new Error(networkError);
        }
      }
      if (err instanceof Error) {
        loggingService.error('API', 'Error in getPublicKey', {
          error: err.message,
          stack: err.stack,
          status: (err as any).status,
        });
        throw err;
      }
      throw new Error(err?.message || 'Failed to retrieve public key');
    }
  },

  // SECURITY FIX: Shared keys are no longer returned by the backend
  // This method now just verifies that key derivation is possible
  // The actual shared key derivation happens server-side during encrypt/decrypt
  async verifySharedKeyDerivation(otherPartyId: string): Promise<void> {
    try {
      const response = await fetch(`${API_BASE_URL}/keys/shared`, {
        method: 'POST',
        headers: await getHeaders(),
        body: JSON.stringify({ receiverId: otherPartyId }),
      });

      if (!response.ok) {
        let errorMessage = 'Failed to verify shared key derivation';
        try {
          const error = await response.json();
          errorMessage = error.error || errorMessage;
        } catch {
          errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      // Backend returns success message, not the actual key
      const data = await response.json();
      console.log('[API] Shared key derivation verified:', data.message);
    } catch (err: any) {
      // Handle various network errors
      if (err instanceof TypeError) {
        if (err.message.includes('fetch') || err.message.includes('Failed to fetch')) {
          throw new Error('Network error: Could not connect to server. Make sure the backend is running on http://localhost:5000');
        }
        if (err.message.includes('sending the request')) {
          throw new Error('Network error: Request failed. Check your internet connection and ensure the backend is running.');
        }
      }
      // Re-throw with original message if it's already an Error
      if (err instanceof Error) {
        throw err;
      }
      // Handle any other error types
      throw new Error(err?.message || 'Failed to verify shared key derivation');
    }
  },

  // SECURITY FIX: Backend now derives shared keys internally
  // We only need to pass the message and receiverId
  async encryptMessage(message: string, receiverId: string): Promise<{ encrypted: string; mac: string }> {
    console.log('[API] encryptMessage called with:', { messageLength: message.length, receiverId });
    try {
      const headers = await getHeaders();
      const body = JSON.stringify({ message, receiverId });
      console.log('[API] Encrypt request URL:', `${API_BASE_URL}/messages/encrypt`);
      console.log('[API] Encrypt request headers:', headers);
      console.log('[API] Encrypt request body length:', body.length);
      
      // Encrypt and generate MAC via backend (uses DLL)
      // Backend derives shared key internally from stored private keys
      const response = await fetch(`${API_BASE_URL}/messages/encrypt`, {
        method: 'POST',
        headers: headers,
        body: body,
      });

      console.log('[API] Encrypt response status:', response.status, response.statusText);
      console.log('[API] Encrypt response headers:', Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        let errorMessage = 'Failed to encrypt message';
        let errorDetails: any = null;
        try {
          errorDetails = await response.json();
          errorMessage = errorDetails.error || errorMessage;
          console.error('[API] Encrypt error response:', errorDetails);
        } catch {
          const text = await response.text();
          console.error('[API] Encrypt error response (text):', text);
          errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();
      console.log('[API] Encrypt success, result keys:', Object.keys(result));
      console.log('[API] Encrypted length:', result.encrypted?.length || 0);
      console.log('[API] MAC length:', result.mac?.length || 0);
      return result;
    } catch (err: any) {
      console.error('[API] encryptMessage error:', err);
      // Handle various network errors
      if (err instanceof TypeError) {
        if (err.message.includes('fetch') || err.message.includes('Failed to fetch')) {
          throw new Error('Network error: Could not connect to server. Make sure the backend is running on http://localhost:5000');
        }
        if (err.message.includes('sending the request')) {
          throw new Error('Network error: Request failed. Check your internet connection and ensure the backend is running.');
        }
      }
      if (err instanceof Error) {
        throw err;
      }
      throw new Error(err?.message || 'Failed to encrypt message');
    }
  },

  // SECURITY FIX: Backend now derives shared keys internally
  // We only need to pass the encrypted content and senderId
  async decryptMessage(encrypted: string, senderId: string): Promise<string> {
    loggingService.trackApiCall('POST', '/messages/decrypt', { senderId, encryptedLength: encrypted.length });
    try {
      const response = await fetch(`${API_BASE_URL}/messages/decrypt`, {
        method: 'POST',
        headers: await getHeaders(),
        body: JSON.stringify({ encrypted, senderId }),
      });

      if (!response.ok) {
        let errorMessage = 'Failed to decrypt message';
        let errorDetails: any = null;
        try {
          errorDetails = await response.json();
          errorMessage = errorDetails.error || errorMessage;
        } catch {
          errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        }
        loggingService.trackApiCall('POST', '/messages/decrypt', { senderId }, null, { error: errorMessage, status: response.status });
        throw new Error(errorMessage);
      }

      const data = await response.json();
      loggingService.trackApiCall('POST', '/messages/decrypt', { senderId }, { decryptedLength: data.decrypted?.length || 0 });
      return data.decrypted;
    } catch (err: any) {
      // Handle various network errors
      if (err instanceof TypeError) {
        if (err.message.includes('fetch') || err.message.includes('Failed to fetch')) {
          const networkError = 'Network error: Could not connect to server. Make sure the backend is running on http://localhost:5000';
          loggingService.error('API', 'Network error in decryptMessage', { error: networkError });
          throw new Error(networkError);
        }
        if (err.message.includes('sending the request')) {
          const networkError = 'Network error: Request failed. Check your internet connection and ensure the backend is running.';
          loggingService.error('API', 'Network error in decryptMessage', { error: networkError });
          throw new Error(networkError);
        }
      }
      if (err instanceof Error) {
        loggingService.error('API', 'Error in decryptMessage', { error: err.message, stack: err.stack });
        throw err;
      }
      const genericError = err?.message || 'Failed to decrypt message';
      loggingService.error('API', 'Unknown error in decryptMessage', { error: genericError });
      throw new Error(genericError);
    }
  },

  async sendMessage(request: CreateMessageRequest): Promise<Message> {
    console.log('[API] sendMessage called with:', {
      senderId: request.senderId,
      receiverId: request.receiverId,
      encryptedLength: request.encryptedContent.length,
      macLength: request.mac.length
    });
    try {
      const headers = await getHeaders();
      const body = JSON.stringify(request);
      console.log('[API] Send message request URL:', `${API_BASE_URL}/messages`);
      console.log('[API] Send message request headers:', headers);
      console.log('[API] Send message request body length:', body.length);
      
      const response = await fetch(`${API_BASE_URL}/messages`, {
        method: 'POST',
        headers: headers,
        body: body,
      });

      console.log('[API] Send message response status:', response.status, response.statusText);
      console.log('[API] Send message response headers:', Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        let errorMessage = 'Failed to send message';
        let errorDetails: any = null;
        try {
          errorDetails = await response.json();
          errorMessage = errorDetails.error || errorMessage;
          console.error('[API] Send message error response:', errorDetails);
        } catch {
          const text = await response.text();
          console.error('[API] Send message error response (text):', text);
          errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();
      console.log('[API] Send message success, result:', {
        id: result.id,
        senderId: result.senderId,
        receiverId: result.receiverId,
        timestamp: result.timestamp
      });
      return result;
    } catch (err: any) {
      console.error('[API] sendMessage error:', err);
      // Handle various network errors
      if (err instanceof TypeError) {
        if (err.message.includes('fetch') || err.message.includes('Failed to fetch')) {
          throw new Error('Network error: Could not connect to server. Make sure the backend is running on http://localhost:5000');
        }
        if (err.message.includes('sending the request')) {
          throw new Error('Network error: Request failed. Check your internet connection and ensure the backend is running.');
        }
      }
      if (err instanceof Error) {
        throw err;
      }
      throw new Error(err?.message || 'Failed to send message');
    }
  },

  // SECURITY FIX: Updated to handle pagination
  async getMessages(clientId?: string, page: number = 1, pageSize: number = 50): Promise<PaginatedMessagesResponse> {
    const url = clientId 
      ? `${API_BASE_URL}/messages/${clientId}?page=${page}&pageSize=${pageSize}`
      : `${API_BASE_URL}/messages?page=${page}&pageSize=${pageSize}`;
    const response = await fetch(url, {
      headers: await getHeaders(),
    });
    if (!response.ok) {
      throw new Error('Failed to fetch messages');
    }
    const data = await response.json();
    // Handle both old format (array) and new format (paginated object)
    if (Array.isArray(data)) {
      return {
        messages: data,
        page: 1,
        pageSize: data.length,
        totalCount: data.length,
      };
    }
    return data;
  },

  // SECURITY FIX: Updated to handle pagination
  // PERFORMANCE FIX: Request server-side decryption to eliminate N decrypt API calls
  async getConversation(clientId1: string, clientId2: string, page: number = 1, pageSize: number = 50): Promise<PaginatedMessagesResponse> {
    // Request decrypted messages from server (decrypt=true)
    const response = await fetch(`${API_BASE_URL}/messages/conversation/${clientId2}?page=${page}&pageSize=${pageSize}&decrypt=true`, {
      headers: await getHeaders(),
    });
    if (!response.ok) {
      throw new Error('Failed to fetch conversation');
    }
    const data = await response.json();
    // Handle both old format (array) and new format (paginated object)
    if (Array.isArray(data)) {
      return {
        messages: data,
        page: 1,
        pageSize: data.length,
        totalCount: data.length,
      };
    }
    return data;
  },

  // User management APIs
  async getCurrentUser(): Promise<UserResponse> {
    const response = await fetch(`${API_BASE_URL}/users/me`, {
      headers: await getHeaders(),
    });
    if (!response.ok) {
      throw new Error('Failed to get current user');
    }
    return response.json();
  },

  async updateUsername(username: string): Promise<UserResponse> {
    const response = await fetch(`${API_BASE_URL}/users/me/username`, {
      method: 'PUT',
      headers: await getHeaders(),
      body: JSON.stringify({ username }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update username');
    }
    return response.json();
  },

  async searchUsers(username?: string): Promise<UserResponse[]> {
    const url = username 
      ? `${API_BASE_URL}/users/search?username=${encodeURIComponent(username)}`
      : `${API_BASE_URL}/users/search`;
    const response = await fetch(url, {
      headers: await getHeaders(),
    });
    if (!response.ok) {
      throw new Error('Failed to search users');
    }
    return response.json();
  },

  async getUserByUsername(username: string): Promise<UserResponse> {
    const token = await getToken();
    const headers = await getHeaders();
    
    console.log('[API] getUserByUsername called with username:', username);
    console.log('[API] Token exists:', !!token);
    console.log('[API] Token preview:', token ? `${token.substring(0, 50)}...` : 'null');
    console.log('[API] Headers:', JSON.stringify(headers, null, 2));
    
    const response = await fetch(`${API_BASE_URL}/users/by-username/${encodeURIComponent(username)}`, {
      headers: headers,
    });
    
    console.log('[API] Response status:', response.status);
    console.log('[API] Response statusText:', response.statusText);
    if (!response.ok) {
      let errorMessage = 'Failed to get user';
      try {
        const error = await response.json();
        errorMessage = error.error || errorMessage;
      } catch {
        if (response.status === 404) {
          errorMessage = 'User not found';
        } else {
          errorMessage = `Failed to get user (${response.status})`;
        }
      }
      throw new Error(errorMessage);
    }
    return response.json();
  },
};
